import { dayjs } from '@revolugo/common/utils'
import { objectPick } from '@vueuse/core'
import { useQuery } from 'villus'

import { GET_EVENT, GET_ORGANIZATION } from '#p-queries'

import type { HotelOfferRequestCreateApi } from '@revolugo/booking-api-client'
import type {
  PublicEvent,
  PublicOrganization,
  PublicSetting,
} from '@revolugo/graphql/public'

async function loadOrganization(tid?: string) {
  if (!tid) {
    return null
  }

  try {
    const { data } = await useQuery<{ organization: PublicOrganization }>({
      query: GET_ORGANIZATION,
      variables: { tid },
    })

    return data.value?.organization
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)

    return null
  }
}

async function loadEvent(
  eventSlug: string | undefined,
  settingSlug: string | undefined,
  tid: string | undefined,
) {
  if (!eventSlug) {
    return null
  }

  try {
    const { data } = await useQuery<{ eventBySlug: PublicEvent }>({
      query: GET_EVENT,
      variables: { eventSlug, settingSlug, tid },
    })

    return data.value?.eventBySlug
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)
  }

  return null
}

async function loadOrganizationAndEvent(
  eventSlug: string | undefined,
  settingSlug: string | undefined,
  tid: string | undefined,
) {
  const [organization, event] = await Promise.all([
    loadOrganization(tid),
    loadEvent(eventSlug, settingSlug, tid),
  ])

  return { event, organization }
}

// eslint-disable-next-line complexity, max-lines-per-function, max-statements
export const useSettings = async (
  query: Record<string, string>,
): Promise<{
  elementsRequest: Ref<Omit<HotelOfferRequestCreateApi, 'eventMetadata'>>
  event: Ref<PublicEvent | undefined>
  request: Ref<HotelOffersRequest>
  settings: Ref<PublicSetting | undefined>
  venue: Ref<
    | {
        description?: string | null
        imgUrl?: string | null
        latitude?: number
        longitude?: number
        name: string
      }
    | undefined
  >
}> => {
  const event = ref<PublicEvent | undefined>()
  const venue = ref<
    | {
        description?: string | null
        imgUrl?: string | null
        latitude?: number
        longitude?: number
        name: string
        size?: number
        tooltip?: string
      }
    | undefined
  >()
  const settings = ref<PublicSetting | undefined>()
  const request = ref<HotelOffersRequest>({
    ...(objectPick(
      query,
      HOTEL_OFFER_REQUEST_QUERY_KEYS,
    ) as unknown as HotelOffersRequest),
  })

  const eventSlug = query.wid as string | undefined
  const settingSlug = query.sid as string | undefined
  const tid = query.tid as string | undefined

  const trackingOrganizationId = ref<string | null>(null)

  const { event: eventData, organization: organizationData } =
    await loadOrganizationAndEvent(eventSlug, settingSlug, tid)

  if (organizationData) {
    trackingOrganizationId.value = organizationData.id
  }

  if (eventData) {
    event.value = eventData
    settings.value = eventData?.setting || undefined

    if (
      event.value?.venueName &&
      event.value?.latitude &&
      event.value?.longitude
    ) {
      venue.value = {
        description: event.value.venueDescription,
        imgUrl: event.value.logoPicture || event.value.markerPicture,
        latitude: event.value.latitude,
        longitude: event.value.longitude,
        name: event.value.venueName,
        size: 70,
        tooltip: event.value.venueName,
      }
    }
  }

  venue.value = {
    description:
      (query.description as string) ||
      venue.value?.description ||
      (query.address as string | undefined),
    imgUrl: (query.marker as string | undefined) || venue.value?.imgUrl,
    latitude: Number(query.latitude) || venue.value?.latitude,
    longitude: Number(query.longitude) || venue.value?.longitude,
    name: query.name || query.venueName || venue.value?.name || '',
    size: 70,
    tooltip: query.name || query.venueName || venue.value?.tooltip,
  }

  let checkInDate = undefined

  if (query.checkInDate) {
    checkInDate = dayjs(query.checkInDate).toDate()
  } else if (event.value?.checkInDate) {
    checkInDate = dayjs(event.value.checkInDate).toDate()
  } else if (event.value?.eventDateFrom) {
    checkInDate = dayjs(event.value?.eventDateFrom).add(-1, 'day').toDate()
  }

  let checkOutDate = undefined

  if (query.checkOutDate) {
    checkOutDate = dayjs(query.checkOutDate).toDate()
  } else if (event.value?.checkOutDate) {
    checkOutDate = dayjs(event.value.checkOutDate).toDate()
  } else if (event.value?.eventDateFrom) {
    checkOutDate = dayjs(event.value.eventDateFrom).toDate()
  }

  if (checkInDate && checkInDate < dayjs().startOf('day').toDate()) {
    checkInDate = dayjs().toDate()
  }

  if (checkOutDate && checkInDate && checkOutDate <= checkInDate) {
    checkOutDate = dayjs(checkInDate).add(1, 'day').toDate()
  }

  request.value = {
    ...request.value,
    checkInDate: (checkInDate && formatQueryDate(checkInDate)) || undefined,
    checkOutDate: (checkOutDate && formatQueryDate(checkOutDate)) || undefined,
    latitude:
      (query.latitude && Number(query.latitude)) ||
      event.value?.latitude ||
      undefined,
    longitude:
      (query.longitude && Number(query.longitude)) ||
      event.value?.longitude ||
      undefined,
  }

  const sanitizedMetadata = Object.keys(query)
    .filter(key => key.startsWith('metadata'))
    .reduce<Record<string, string>>((acc, key) => {
      acc[key.replace('metadata', '')] = query[key]
      return acc
    }, {})

  updateConfig(
    buildElementsConfig({
      event: event.value,
      eventMetadataOverride: {
        dateEnd: query.eventEndDate,
        dateStart: query.eventStartDate,
        imageURL: query.logo || undefined,
        name: query.name || query.venueName,
      },
      metadata: sanitizedMetadata,
      organizationId: trackingOrganizationId.value || query.oid,
      setting: settings.value,
      theme: {
        markerPrimary: query.colorWidgetHotelMarker || query.colorHotelMarker,
        markerSecondary:
          query.colorWidgetVenueMarker ||
          query.colorVenueMarker ||
          (venue.value.imgUrl ? '#FFFFFF' : undefined),
        primary: query.colorWidgetMain_1 || query.colorPrimary,
        secondary: query.colorWidgetMain_2 || query.colorSecondary,
      },
      trackingId: query.tid,
      wid: query.wid,
    }),
  )

  if (typeof window !== 'undefined') {
    window.addEventListener('message', e => {
      if (
        (e.data.markerB64 || e.data.markerB64 === '') &&
        venue.value?.latitude &&
        venue.value?.longitude
      ) {
        venue.value = {
          ...venue.value,
          imgUrl: e.data.markerB64,
        }
      }
    })
  }

  const elementsRequest = computed(() =>
    buildElementsHotelOffersRequest(request.value),
  )

  return {
    elementsRequest,
    event,
    request,
    settings,
    venue,
  }
}
